<script setup lang="ts">
import { LEVEL_OPTIONS } from '#shared/utils/constants'
import { tv } from 'tailwind-variants'

const packet = inject(keys.currentPacket)

const variants = tv({
  slots: {
    titleCard: '',
    trialCard: '',
    freeCircle: '',
  },
  variants: {
    theme: {
      'green-seagreen': {
        titleCard: 'bg-seagreen-50 text-white',
        trialCard: 'border-seagreen-pop text-white',
        freeCircle: 'text-yellow-pop',
      },
      'dark-red-pink': {
        titleCard: 'bg-coral-40 text-white',
        trialCard: 'border-coral-pop text-white',
        freeCircle: 'text-yellow-pop',
      },
      'blue-pink': {
        titleCard: 'bg-blue-50 text-coral-legacy-20',
        trialCard: 'border-coral-legacy-20 text-white',
        freeCircle: 'text-coral-pop',
      },
      'green-red': {
        titleCard: 'bg-green-40 text-white',
        trialCard: 'border-green-pop text-white',
        freeCircle: 'text-coral-20',
      },
    },
  },
})

const classes = computed(() => variants({ theme: unref(packet)?.theme }))

const formatter = new Intl.ListFormat('nb-NO', {
  style: 'long',
  type: 'conjunction',
})

const gradeDescription = computed(() => {
  const levels = unref(packet)?.levels ?? []
  return formatter.format(
    levels.map((level) => {
      const option = LEVEL_OPTIONS.find((o) => o.value === level)
      // For VGS, we still want the abbreviated text
      const label = level.toLowerCase() === 'vgs' ? option?.tagName : option?.title
      return label || level
    }),
  )
})

const toast = useUiToast()

const cart = useCartStore()

const route = useRoute()

const isAdding = ref(false)

const trials = useLicenses(/* FIXME: Re-add predicate when https://acoundervisning.atlassian.net/browse/RE-1213 has been resolved: (it) => it.licenseModel === "TRIAL_SCHOOL" */)

const auth = useAuthorizeStore()

// Must be kept manually in sync with the same variable in CORE
// https://gitlab.com/aschehoug/core/aco-core-api/-/blob/main/core-ecommerce-api/src/main/java/no/aschehoug/core/ecommerce/controller/CheckoutController.java#L33
const TRIAL_LICENSE_GRACE_PERIOD_DAYS = 90

async function handleAddTrial(relation: ProductVariantRelation) {
  if (!auth.loggedIn) {
    return navigateToLogin(route)
  }

  const variant = unref(packet)?.variants.find((it) => it.sku === relation.sku)

  if (!variant) {
    handleError('No trial found', 'No trial found', false)
    toast.error('Noe gikk galt, ta kontakt med kundeservice.')
    return
  }

  if (!variant.isTrial) {
    handleError('Not a trial', 'Not a trial', false)
    toast.error('Noe gikk galt, ta kontakt med kundeservice.')
    return
  }

  isAdding.value = true

  try {
    const { data } = await trials.suspense()

    const pastTrial = data?.find((license) => license.ean === variant.sku)

    let warningMessage: string | undefined

    if (pastTrial) {
      if (!pastTrial.endDate || !isPast(new Date(pastTrial.endDate))) {
        warningMessage = 'Skolen din har en aktiv prøvelisens.'
      } else if (isPast(new Date(pastTrial.endDate)) && differenceInDays(new Date(pastTrial.endDate), new Date()) < TRIAL_LICENSE_GRACE_PERIOD_DAYS) {
        warningMessage = 'Skolen din har hatt prøvelisens innenfor de siste tre måneder.'
      }
    }

    if (warningMessage) {
      toast.info({
        message: h(
          'div',
          [
            h('p', `${warningMessage} Vi tilbyr tidligst ny gratisperiode når det er gått 90 dager siden forrige tok slutt.`),
            h('br'),
            h('p', 'Ønsker dere allikevel ny prøveperiode nå? Kontakt kundeservice for forespørsel om prøvelisens.'),
          ],
        ),
        persistent: true,
        duration: 90_000,
        buttons: [
          {
            text: 'Kontakt kundeservice',
            onClick: () => navigateTo({
              name: 'kundeservice',
            }),
          },
        ],
      })
    } else {
      await cart.add({
        variant,
        amount: 1,
      })
    }
  } finally {
    isAdding.value = false
  }
}

// TODO: Texts from CMS
</script>

<template>
  <section
    v-if="packet"
    class="@container w-full bg-inherit pt-legacy-m pb-5"
  >
    <UContainer class="relative z-0 mx-auto flex flex-col gap-legacy-m bg-inherit md:gap-[30px] @md:flex-row">
      <div :class="[classes.titleCard(), { 'basis-1/2': !!packet.trialCard }]" class="flex h-72 w-full flex-col items-center justify-center gap-7 rounded-tl-[4.5rem] px-10 py-legacy-xl md:px-16 lg:px-28">
        <CrystallizeImage
          v-if="packet?.logo"
          :image="packet?.logo"
          :alt="packet.logo.altText || packet.title || packet.name"
          class="w-full max-w-72"
          img-class="w-full"
        />
        <h1
          v-else
          class="text-4xl md:text-5xl"
          v-text="packet.title || packet.name"
        />
        <h2
          v-if="gradeDescription"
          class="text-2xl md:text-3xl lg:text-4xl"
          v-text="gradeDescription"
        />
      </div>
      <template v-if="packet.trialCard">
        <div
          class="flex basis-1/2 flex-col items-center justify-center gap-4 rounded-br-[4.5rem] border bg-inherit px-9 py-8 text-center md:gap-7"
          :class="classes.trialCard()"
        >
          <h3 class="text-lg font-bold md:text-xl lg:text-2xl" v-text="packet.trialCard.title" />
          <div
            v-if="packet.trialCard.text"
            class="text-sm lg:text-xl"
            v-html="packet.trialCard.text"
          />
          <UButton
            class="text-md justify-center rounded-full bg-yellow-legacy-40 px-legacy-s py-legacy-xs text-sm text-black-80 no-underline hover:bg-yellow-legacy-30 disabled:bg-gray-20 disabled:text-gray-50"
            :disabled="isAdding"
            @click="handleAddTrial(packet.trialCard.variant)"
          >
            Prøv gratis
            <UIcon
              v-if="isAdding"
              name="i-lucide:loader-circle"
              class="size-5 animate-spin text-current"
            />
          </UButton>
        </div>
        <SvgFreeCircle
          class="absolute -right-14 -bottom-14 z-[-1] scale-125 rotate-on-scroll"
          aria-hidden="true"
          :class="classes.freeCircle()"
        />
      </template>
    </UContainer>
  </section>
</template>
